import { useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { PlayCircleOutline } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import {
  buildRemoteSpeakerConsoleUrl,
  useRemoteSpeakerLaunchMutation,
} from '@/state/mutations/player/use-remote-speaker-launch';

/**
 * Remote speaker slot: open the live console. The window opens on the click (pop-up blockers allow it) and
 * is pointed at the console once the server has issued the single-use code.
 */
export function JoinLiveButton({ contributorId, roleSlotId }: { contributorId: string; roleSlotId: string }) {
  const t = useTranslations();
  const launch = useRemoteSpeakerLaunchMutation();
  const [failed, setFailed] = useState(false);

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setFailed(false);
    const consoleWindow = window.open('about:blank', '_blank');
    launch.mutate(
      { contributorId, roleSlotId },
      {
        onSuccess: ({ code }) => {
          const url = buildRemoteSpeakerConsoleUrl(code);
          if (consoleWindow) {
            consoleWindow.opener = null;
            consoleWindow.location.href = url;
          } else {
            window.location.href = url;
          }
        },
        onError: () => {
          consoleWindow?.close();
          setFailed(true);
        },
      },
    );
  };

  return (
    <Stack spacing={0.5} sx={{ py: 0.5, width: '100%' }} onClick={(event) => event.stopPropagation()}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
        <Button
          size="small"
          variant="contained"
          color="error"
          startIcon={<PlayCircleOutline />}
          onClick={handleClick}
          disabled={launch.isPending}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {t('user.contributionsList.joinLive')}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
          {t('user.contributionsList.joinLiveNotice')}
        </Typography>
      </Stack>
      {failed ? (
        <Alert severity="warning" sx={{ py: 0 }}>
          {t('user.contributionsList.joinLiveUnavailable')}
        </Alert>
      ) : null}
    </Stack>
  );
}
